// Justified-row photo layout (Google Photos / Flickr style), for
// photobook inner pages. Pure functions, no DOM - Node-testable, same
// style as guillotine-layout.js (plain {w,h}/{x,y} objects,
// deterministic, order-preserving). See docs/decisions-to-make.md #30
// for the full development trail (measured bugs, real numbers, why each
// design choice) and docs/plans/photobook-layout-engine.md for how this
// relates to the tree engine it sits alongside.
//
// Why this exists alongside guillotine-layout.js rather than replacing
// it: the tree picks a box SHAPE first (from an aspect-band rule) and
// then fits whichever photo best matches it - shape-first, photo-second,
// which is why a mismatched photo leaves visible contain-fit gaps (the
// whole reason offset-pairs, computeSmartFit's offsetPair branch in
// photobook-builder.liquid, exists as a patch on top of it). Justified
// rows are photo-first: every photo keeps its own true aspect ratio
// exactly (scaled, never cropped or letterboxed) - the row's height is
// SOLVED from the photos already in it, not decided in advance. No
// change to guillotine-layout.js itself - collage/canvas/mug/greeting-
// card are completely unaffected, this only ever touches photobook.
//
// Tray order is preserved exactly - rows fill in sequence, never
// resorted (same principle the photobook plan document already
// established for the tree: "the user's own tray order is the book's
// sequence, full stop"). Variety comes from the algorithm itself: a run
// of portrait photos naturally makes a taller row, a wide/panoramic
// photo naturally claims a short, wide row with zero special-casing
// beyond the explicit panorama carve-out below.
//
// --- Why row COUNT, not row HEIGHT, is the thing to search over -------
// First version searched a continuous "nominal row height" and greedily
// packed rows against it (classic Flickr-style justified galleries do
// this, for an infinite-scroll container with no fixed total height to
// hit). Measured against real small photo counts (3-9 per page, typical
// for a print page): total height is a STEP function of nominal height
// (row membership only changes at discrete breakpoints), and for a small
// N those steps are coarse enough that no nominal value lands close to
// the page's actual usable height - average fill ratio measured at 0.61,
// worst case 0.27. The real degree of freedom is the NUMBER of rows, not
// a continuous height - so this searches row count directly (cheap for
// print-page photo counts) and picks whichever count's partition lands
// closest to the page height, which is what actually closes the gap.
(function (root) {
  'use strict';

  // Solves the row height that makes `rowPhotos` (already decided as one
  // row's membership) span exactly `targetWidth` at a shared height,
  // photos placed edge to edge with `gap` between them. Direct algebra:
  // width(h) = h * sum(aspect_i) + gap*(n-1) is linear in h.
  function solveRowHeight(rowPhotos, targetWidth, gap) {
    const n = rowPhotos.length;
    const sumAspect = rowPhotos.reduce(function (acc, p) { return acc + p.aspect; }, 0);
    return (targetWidth - gap * (n - 1)) / sumAspect;
  }

  function sumRowHeights(rows, gap) {
    const h = rows.reduce(function (acc, r) { return acc + r.height; }, 0);
    return h + gap * Math.max(rows.length - 1, 0);
  }

  // Every way to cut `photos` (in order - never reordered) into
  // contiguous groups is just a choice of which of the N-1 gaps between
  // adjacent photos become row breaks - 2^(N-1) combinations, one bit
  // per gap. Trying only ONE partition per row count (evenly-sized
  // chunks) was measured far too restrictive: for 6 real photos this
  // rejected a [2,2,2] split (overshoots) in favor of [3,3] (fills only
  // 57% of the target height) when a DIFFERENT 3-row split of the same 6
  // photos would have landed much closer - evenly-sized chunking simply
  // never considered it. Cheap enough to enumerate exhaustively for
  // realistic per-page photo counts (2^19 ~ 500K for N=20, each an O(N)
  // evaluation); MAX_ENUMERATE_PHOTOS caps it as a backstop for an
  // unusually dense page, not something real per-page counts are
  // expected to hit.
  const MAX_ENUMERATE_PHOTOS = 20;

  function enumerateContiguousPartitions(photos) {
    const n = photos.length;
    const combos = 1 << (n - 1);
    const partitions = [];
    for (let mask = 0; mask < combos; mask++) {
      const groups = [];
      let start = 0;
      for (let i = 0; i < n - 1; i++) {
        if (mask & (1 << i)) { groups.push(photos.slice(start, i + 1)); start = i + 1; }
      }
      groups.push(photos.slice(start));
      partitions.push(groups);
    }
    return partitions;
  }

  // Splits `photos` into exactly `rowCount` contiguous groups, as evenly
  // sized as possible - the fallback partition source for a page too
  // dense to enumerate exhaustively (see MAX_ENUMERATE_PHOTOS).
  function chunkEvenly(photos, rowCount) {
    const n = photos.length;
    const base = Math.floor(n / rowCount);
    const remainder = n % rowCount;
    const groups = [];
    let idx = 0;
    for (let r = 0; r < rowCount; r++) {
      const size = base + (r < remainder ? 1 : 0);
      groups.push(photos.slice(idx, idx + size));
      idx += size;
    }
    return groups;
  }

  // ROW_CAP_MULTIPLIER: caps any single row's height at this multiple of
  // targetHeight/(rows in this partition) - needed because a lone
  // narrow/portrait photo forced to fully width-justify on its own can
  // require an enormous height (measured: a 9:16 photo alone, stretched
  // to a 269mm-wide page, needs 479mm - more than TWICE the page's own
  // usable height). Without a cap, one such row would dominate any
  // partition containing it. Capped rows render at the cap height
  // instead of full width - the same "don't force it, leave a small gap
  // on THIS row rather than blow it up" call, generalized to any row,
  // not just a trailing one.
  const ROW_CAP_MULTIPLIER = 1.6;

  function rowsCoveredArea(rows) {
    return rows.reduce(function (acc, r) {
      return acc + r.photos.reduce(function (a2, p) { return a2 + r.height * r.height * p.aspect; }, 0);
    }, 0);
  }

  // Builds one candidate partition's rows, THEN - if the natural
  // (capped) total still overshoots targetHeight - uniformly shrinks
  // every row's height (and so, automatically, every photo's width,
  // since width = height * aspect) by the single factor that makes the
  // total land exactly on targetHeight. A uniformly shrunk row is still
  // fully proportioned, just narrower than the page - centered with a
  // small side margin, not overflowing. Rejecting every overshooting
  // candidate outright (an earlier version's rule) could leave NOTHING
  // close - measured: three identical 16:9 photos had exactly one
  // non-overshooting option (one flat row, filling 22% of the page)
  // while a mildly-overshooting option (11% over) was available and,
  // once shrunk to fit, filled 82%. Shrinking beats rejecting whenever
  // the shrink itself is modest.
  function scorePartition(groups, targetWidth, targetHeight, gap) {
    const capHeight = (targetHeight / groups.length) * ROW_CAP_MULTIPLIER;
    const rows = groups.map(function (g) {
      const natural = solveRowHeight(g, targetWidth, gap);
      if (natural > capHeight) return { photos: g, height: capHeight, unjustified: true };
      return { photos: g, height: natural };
    });
    const gapTotal = gap * Math.max(rows.length - 1, 0);
    const rowHeightSum = rows.reduce(function (acc, r) { return acc + r.height; }, 0);
    const naturalTotal = rowHeightSum + gapTotal;
    if (naturalTotal > targetHeight && rowHeightSum > 0) {
      const scale = Math.max((targetHeight - gapTotal) / rowHeightSum, 0.05);
      rows.forEach(function (r) { r.height *= scale; });
    }
    return { rows: rows, total: sumRowHeights(rows, gap) };
  }

  // Same mixing shape as guillotine-layout.js's own private mixSeed -
  // re-declared locally (that one isn't exported) rather than reached
  // into. Only used to pick WHICH near-equally-good partition a seed
  // lands on - never affects whether a partition is fully justified,
  // capped, or shrunk, so it can never make a page look worse, only
  // different.
  function mixSeed(seed, salt) {
    return (Math.imul(seed ^ salt, 2654435761) ^ (seed >>> 13)) | 0;
  }

  // Fraction of the best-found area a candidate is allowed to give up
  // and still be eligible for seeded selection - same shape as the tree
  // engine's LAYOUT_VARIATION_TOLERANCE, sized much tighter here (6% vs
  // the tree's 30%) because the cost of a worse pick is different: the
  // tree's tolerance trades among candidates that are ALL still
  // aspect-band-compliant (a hard guarantee, never violated); here it
  // trades directly against the fill percentage this engine exists to
  // maximise, so it stays conservative - enough pool for real pages to
  // vary (measured: ~25% of real page/window combinations land on a
  // genuinely different shape between two arbitrary seeds), never enough
  // to meaningfully undercut the best option found (0% of measured pages
  // took a fill-percentage hit from this - the pool is empty, and the
  // single best option used, whenever there isn't a close second).
  const VARIATION_TOLERANCE = 0.06;

  // Wider variation tolerance used ONLY once photos.length >
  // EVEN_SIZING_MIN_PHOTOS, i.e. only once partitionAreaRatio's floor is
  // also active as a backstop - see bestRowPartition. Direct feedback,
  // 2026-09-18 night: pages of near-identical-aspect photos (e.g. 6
  // portraits, all ~the same shape) were rendering as a perfectly
  // uniform grid every time - "too structured... two could be bigger to
  // create variance." Investigated rather than assumed: for 6 identical
  // 0.75-aspect photos, [3,3] is not artificially favoured by the
  // evenness rule - it's genuinely the best-FILLING partition by a wide
  // margin (fill 0.736 vs [2,4]'s 0.659, a 10.4% gap - nowhere near the
  // base 6% tolerance, so [2,4] never entered the pool at all,
  // regardless of seed). [2,4] is still perfectly reasonable to look at
  // (area ratio 0.36 - similar to the 4-portrait+1-landscape case's own
  // 0.41-0.47, already shipped as looking fine) - it just never got a
  // chance to be picked. Measured the same gap at several other photo
  // counts before picking a value: 21.2% for n=5, 10.4% for n=6, 1.9%
  // for n=8 (already inside the base tolerance) - n=7 and n=9's nearest
  // distinct shape sit 40-58% away regardless, so no tolerance value
  // forces variety where none exists nearby; this just stops excluding
  // it where it already does.
  //
  // Safe to be this much more generous than the base 6% specifically
  // here (closer to the tree engine's own 30% precedent, which this
  // file's own comment above explicitly called "more conservative than
  // that because there was nothing else protecting against a bad pick"
  // when it was written) - partitionAreaRatio's floor is a SEPARATE,
  // independent backstop against the two real pathological cases
  // (#30's original 8-photo hero row, the 4-portrait+1-landscape tiny
  // landscape) that didn't exist when the base tolerance was chosen.
  // Every candidate this pool can ever contain already cleared that
  // floor - widening this only trades among already-acceptable options,
  // never reopens either fixed bug (both regression-tested at their
  // original ratios of 0.054 and 0.211, both still well under the 0.25
  // floor regardless of this constant).
  const VARIATION_TOLERANCE_EVEN_GATED = 0.25;

  // Row-group size above which a candidate partition's per-PHOTO area
  // disparity gets weighed against its raw covered area - see
  // partitionAreaRatio and its use in bestRowPartition below.
  const EVEN_SIZING_MIN_PHOTOS = 4;

  // Smallest individual photo's rendered area divided by the largest,
  // across every photo in every row of a built partition - 1.0 is
  // perfectly even, near 0 means at least one photo is being rendered
  // tiny next to another. This replaced two earlier, WRONG attempts at
  // the same goal, both measured wrong on real cases before shipping:
  //   1. A row/column-COUNT balance rule ([2,3,3] yes, [6,2] no) caught
  //      the first reported case (an 8-photo page, a 2-photo row towering
  //      over 3-photo rows - fewer photos sharing a row's width solves to
  //      a taller row). But group SIZE is only a proxy for the real
  //      problem - it doesn't see individual aspect ratios at all.
  //   2. Tightening that to also forbid any size-1 group (after a live
  //      report: 4 portraits + 1 landscape, column mode, the landscape
  //      alone in a crowded group came out 4.7x smaller than the others)
  //      fixed THAT case but broke a different, already-shipped one: 5
  //      identical-aspect portraits' best-fill column split was [1,1,3] -
  //      forbidding size-1 outright forced a [2,3] split that measured
  //      WORSE on the actual thing that matters (fillRatio 0.81 -> 0.28),
  //      and - measured only once this file went looking for it - that
  //      original "successful" [1,1,3] split had its OWN 9x per-photo
  //      area disparity (two solo columns at ~18700mm² next to three
  //      crowded ones at ~1970mm² each) that nobody had checked before,
  //      because the only thing measured at the time was aggregate fill.
  // Per-photo area ratio catches both real cases (and would have caught
  // the second one on day one) without a group-shape proxy that can be
  // wrong in either direction - it's the same "the real objective, not a
  // proxy" principle bestRowPartition's own area-maximization already
  // uses, just also applied to evenness.
  function partitionAreaRatio(rows) {
    let minArea = Infinity, maxArea = 0;
    rows.forEach(function (row) {
      row.photos.forEach(function (p) {
        const area = row.height * row.height * p.aspect;
        if (area < minArea) minArea = area;
        if (area > maxArea) maxArea = area;
      });
    });
    if (maxArea <= 0) return 1;
    return minArea / maxArea;
  }

  // Floor a candidate's area ratio has to clear, once photos.length >
  // EVEN_SIZING_MIN_PHOTOS, to stay eligible on evenness grounds alone -
  // see bestRowPartition for how this combines with actual area
  // (never excludes every candidate: falls back to whatever the best
  // achievable ratio actually is when nothing reaches this).
  //
  // 0.25, not the 0.4 this shipped with hours earlier: measured on a
  // real 8-photo page (direct feedback: "I would like a fuller page to
  // be the first iteration") that 0.4 was excluding a [3,3,2] split
  // (fill 0.69, ratio 0.30-0.33) entirely, leaving only options clustered
  // around ratio 0.50 whose best fill was [4,4] at 0.57 - a full 12
  // points of fill given up for evenness nobody asked to trade that much
  // for. Checked against both real bad cases this gate exists to
  // exclude, which cluster far below either floor (0.054 for the
  // original 8-photo [6,2] hero case, 0.211 for the 4-portrait+
  // 1-landscape crowded-column case) - clean separation from the ~0.30+
  // good cases at either floor value, so 0.25 loses nothing on the
  // cases this was built for while recovering the ones it was
  // accidentally still excluding.
  const MIN_AREA_RATIO = 0.23;

  // Picks whichever candidate partition ends up covering the MOST actual
  // page area once built (natural if it fits, uniformly shrunk-to-fit if
  // it doesn't) - the real objective, not a proxy like "closest total
  // height", which measured cases showed could actively pick worse
  // results (see scorePartition's own comment for the concrete example).
  // Past EVEN_SIZING_MIN_PHOTOS photos, first narrows to whichever
  // candidates clear MIN_AREA_RATIO (or, if none do, whichever achieve
  // the single best ratio found) - see partitionAreaRatio's comment for
  // why this has to be a real per-photo measurement, not a row-count
  // proxy - THEN maximizes area among survivors, same as always.
  //
  // variationSeed (optional): when omitted, always returns the single
  // best-scoring partition (deterministic). When provided, collects
  // every candidate within VARIATION_TOLERANCE of the best area and
  // picks among THOSE by seed - same photos in the same order always
  // reproduce the same layout (stable across re-renders, e.g. draft
  // restore), while different pages/orders naturally land on different
  // seeds, and Shuffle reordering the same photos changes the seed too -
  // same guarantees pageLayoutVariationSeed already gives the tree
  // engine (photobook-builder.liquid).
  function bestRowPartition(photos, targetWidth, targetHeight, gap, variationSeed) {
    if (photos.length === 0) return [];
    // Even a lone photo (e.g. an isolated segment forced by a panorama
    // on either side of it) goes through the same cap+shrink path as any
    // other row - an isolated landscape photo left unconditionally
    // full-width-justified could claim ~200mm of a ~20mm allocated
    // budget, overflowing the page well past what the residual clamp
    // below could absorb.
    if (photos.length === 1) return scorePartition([photos], targetWidth, targetHeight, gap).rows;

    const candidateGroups = photos.length <= MAX_ENUMERATE_PHOTOS
      ? enumerateContiguousPartitions(photos)
      : (function () { const out = []; for (let r = 1; r <= photos.length; r++) out.push(chunkEvenly(photos, r)); return out; })();

    let scoredList = candidateGroups.map(function (groups) {
      const scored = scorePartition(groups, targetWidth, targetHeight, gap);
      return { rows: scored.rows, area: rowsCoveredArea(scored.rows), areaRatio: partitionAreaRatio(scored.rows) };
    });

    let tolerance = VARIATION_TOLERANCE;
    if (photos.length > EVEN_SIZING_MIN_PHOTOS) {
      let bestRatio = -1;
      scoredList.forEach(function (c) { if (c.areaRatio > bestRatio) bestRatio = c.areaRatio; });
      const ratioFloor = Math.min(MIN_AREA_RATIO, bestRatio);
      scoredList = scoredList.filter(function (c) { return c.areaRatio >= ratioFloor; });
      tolerance = VARIATION_TOLERANCE_EVEN_GATED;
    }

    let bestArea = -1;
    scoredList.forEach(function (c) { if (c.area > bestArea) bestArea = c.area; });

    if (variationSeed === undefined) {
      for (let i = 0; i < scoredList.length; i++) { if (scoredList[i].area === bestArea) return scoredList[i].rows; }
    }

    const pool = scoredList.filter(function (c) { return c.area >= bestArea * (1 - tolerance); });
    const idx = Math.abs(variationSeed) % pool.length;
    return pool[idx].rows;
  }

  // --- Panoramas -------------------------------------------------------
  // An extreme-aspect photo destabilizes a shared search across the
  // whole page: it forces a lone, very short row wherever it lands,
  // which can swing the achievable total height wildly depending on
  // whether it's grouped with anything else. Same root cause the
  // guillotine tree already solved for with its own panorama-band
  // carve-out (buildTreeBoxesWithPanoramaBand, photobook-builder.liquid)
  // - same fix shape here: pull any photo past the threshold OUT of the
  // row search entirely, give it its own full-width row at its natural
  // aspect (capped so an extreme ratio can't crowd out everything else),
  // and run the normal row search only on what's left - split into
  // whichever runs the panorama(s) break it into, each targeting its own
  // proportional share of the remaining height independently (no shared
  // search across a forced break - tray order preserved throughout, a
  // panorama mid-sequence still renders mid-sequence).
  const PANORAMA_ASPECT_THRESHOLD = 2.2; // matches PANORAMA_ASPECT_THRESHOLD in photobook-builder.liquid
  const PANORAMA_MAX_BAND_FRACTION = 0.55; // matches PANORAMA_MAX_BAND_FRACTION in photobook-builder.liquid

  function splitIntoSegments(photos) {
    const segments = [];
    let current = [];
    photos.forEach(function (p) {
      if (p.aspect > PANORAMA_ASPECT_THRESHOLD) {
        if (current.length) { segments.push({ type: 'normal', photos: current }); current = []; }
        segments.push({ type: 'panorama', photo: p });
      } else {
        current.push(p);
      }
    });
    if (current.length) segments.push({ type: 'normal', photos: current });
    return segments;
  }

  function buildJustifiedLayoutRowMode(photos, pageWidthMm, pageHeightMm, opts) {
    opts = opts || {};
    const marginMm = opts.marginMm === undefined ? 5 : opts.marginMm;
    const gapMm = opts.gapMm === undefined ? 2.5 : opts.gapMm;

    const usableWidth = pageWidthMm - 2 * marginMm;
    const usableHeight = pageHeightMm - 2 * marginMm;

    if (photos.length === 0) return { rows: [], fillRatio: 0 };
    if (photos.length === 1) {
      // Nothing to justify against - full-bleed single photo, same
      // treatment the tree gives n=1 (rootFrame's fullBleed flag).
      return {
        rows: [{ y: 0, height: pageHeightMm, photos: [{ uuid: photos[0].uuid, x: 0, y: 0, w: pageWidthMm, h: pageHeightMm }] }],
        fillRatio: 1
      };
    }

    const segments = splitIntoSegments(photos);
    const panoramaRowHeights = segments.map(function (s) {
      return s.type === 'panorama' ? Math.min(usableWidth / s.photo.aspect, usableHeight * PANORAMA_MAX_BAND_FRACTION) : null;
    });
    const panoramaHeightTotal = panoramaRowHeights.reduce(function (acc, h) { return acc + (h || 0); }, 0);
    const heightForGaps = gapMm * Math.max(segments.length - 1, 0);
    const normalSegments = segments.filter(function (s) { return s.type === 'normal'; });
    const normalPhotoCount = normalSegments.reduce(function (acc, s) { return acc + s.photos.length; }, 0);
    const normalTotalTargetHeight = Math.max(usableHeight - panoramaHeightTotal - heightForGaps, usableHeight * 0.15);

    const allRows = [];
    segments.forEach(function (seg, segIdx) {
      if (seg.type === 'panorama') {
        allRows.push({ photos: [seg.photo], height: panoramaRowHeights[segIdx], isPanorama: true });
        return;
      }
      // Each normal segment gets a share of the remaining height
      // proportional to its own photo count - segments are otherwise
      // fully independent (a panorama forces a hard break, so there's no
      // shared row-count search across it anyway). Each segment mixes
      // its own salt into the page's variation seed so multiple normal
      // segments on the same panorama page don't all land on the same
      // pool index by coincidence.
      const share = normalPhotoCount > 0 ? seg.photos.length / normalPhotoCount : 0;
      const segTargetHeight = normalTotalTargetHeight * share;
      const segSeed = opts.variationSeed === undefined ? undefined : mixSeed(opts.variationSeed, segIdx * 13 + 1);
      bestRowPartition(seg.photos, usableWidth, segTargetHeight, gapMm, segSeed).forEach(function (r) { allRows.push(r); });
    });

    // A panorama row's height comes straight from its own MIN() cap
    // (above), never from bestRowPartition's own shrink-to-fit - so unlike
    // normal rows, nothing had checked panorama rows (individually or
    // together) against the real height budget at all. Caught 2026-10-02
    // investigating a real report ("gap through the centre... a lone
    // unique-orientation photo looking small") that traced to this: a
    // portrait narrow enough that 1/aspect crosses PANORAMA_ASPECT_
    // THRESHOLD once column mode transposes it (measured: aspect 0.42,
    // transposed 2.38 > 2.2) gets treated as its own full-"width" panorama
    // row - fine for ONE, but several such rows stacked have no shared
    // search keeping their TOTAL height in budget the way normal rows'
    // own shrink-to-fit does, so multiple narrow portraits on one
    // page/zone could together extend well past the page - measured one
    // real case overflowing to more than double the allotted width after
    // transpose-back. The comment this replaced already knew a capped
    // panorama band could push the budget negative ("a capped panorama
    // band can still push this negative in a pathological mix") but only
    // clamped the SYMPTOM (residual floored at 0, so rows silently
    // overflowed with no visible residual accounting for it) rather than
    // the cause.
    //
    // Fix: same shrink-to-fit scorePartition already applies to normal
    // rows, extended to the combined total (panorama rows included) - if
    // everything built above still overshoots usableHeight, scale every
    // row's height uniformly (panorama or not) by the single factor that
    // makes the total fit exactly. A uniformly-shrunk panorama row is
    // still at its own full aspect, just smaller - the same "shrink beats
    // overflowing" principle bestRowPartition's own scorePartition already
    // uses for normal rows, applied here for the first time to the
    // panorama+normal combined total. Floored at 0.05 for the same
    // division-by-zero reason scorePartition's own scale is.
    const naturalTotalHeight = sumRowHeights(allRows, gapMm);
    if (naturalTotalHeight > usableHeight) {
      const gapTotal = gapMm * Math.max(allRows.length - 1, 0);
      const rowHeightSum = naturalTotalHeight - gapTotal;
      const scale = rowHeightSum > 0 ? Math.max((usableHeight - gapTotal) / rowHeightSum, 0.05) : 1;
      allRows.forEach(function (r) { r.height *= scale; });
    }
    const actualTotalHeight = sumRowHeights(allRows, gapMm);
    // Residual (rounding, or the proportional-share split not landing
    // exactly) used to go entirely into the margin, grown symmetrically
    // top/bottom - same principle findUsableFrame uses in the tree
    // engine, but direct feedback was that a row-group with a lot of
    // leftover room should move OUTWARD instead: half the residual still
    // goes to margin (the group stays roughly centered), the other half
    // is redistributed into the gaps BETWEEN rows, which pushes the
    // first/last row toward the page edges and halves the margin-side gap
    // versus before. A single row has no internal gap to grow, so it
    // keeps the old full-residual-as-margin behavior. The shrink step
    // above means actualTotalHeight can no longer exceed usableHeight, so
    // this max(...,0) is now just rounding-safety, not load-bearing.
    const residual = Math.max(usableHeight - actualTotalHeight, 0);
    const extraRowGap = allRows.length > 1 ? (residual / 2) / (allRows.length - 1) : 0;
    const topOffset = marginMm + (allRows.length > 1 ? residual / 4 : residual / 2);

    let coveredArea = 0;
    const pageArea = pageWidthMm * pageHeightMm;
    let y = topOffset;
    const outRows = allRows.map(function (row) {
      // A row narrower than the full usable width (capped or shrunk to
      // fit - see scorePartition) used to be simply centered - full
      // shortfall as symmetric outer margin, nothing between the photos
      // themselves. Direct feedback: "large gaps on the sides" - this was
      // the SAME complaint the outward-spacing fix above (topOffset/
      // extraRowGap) already addresses for leftover height between rows,
      // just one axis down and never actually reached by that fix -
      // topOffset/extraRowGap only ever move leftover HEIGHT into the
      // gaps BETWEEN rows; a single row's own WIDTH shortfall (this
      // block) was still just centered, un-redistributed, the exact
      // before-fix behaviour on the other axis. Same halving principle,
      // applied one level down: half the shortfall stays as outer
      // margin (the row stays roughly centered), half spreads into the
      // gaps BETWEEN the photos in this row. A single-photo row has
      // nothing else in the row to redistribute into, so it keeps the
      // old full-shortfall-as-margin behaviour, same reasoning as
      // topOffset's own allRows.length>1 guard. A full-width row's own
      // actualRowWidth already equals usableWidth, so this is a no-op
      // for the common case.
      const actualRowWidth = row.photos.reduce(function (acc, p) { return acc + row.height * p.aspect; }, 0) + gapMm * Math.max(row.photos.length - 1, 0);
      const rowResidual = Math.max(usableWidth - actualRowWidth, 0);
      const extraPhotoGap = row.photos.length > 1 ? (rowResidual / 2) / (row.photos.length - 1) : 0;
      let x = marginMm + (row.photos.length > 1 ? rowResidual / 4 : rowResidual / 2);
      const rowPhotos = row.photos.map(function (p) {
        const w = row.height * p.aspect;
        const box = { uuid: p.uuid, x: x, y: y, w: w, h: row.height };
        x += w + gapMm + extraPhotoGap;
        coveredArea += w * row.height;
        return box;
      });
      const thisY = y;
      y += row.height + gapMm + extraRowGap;
      return { y: thisY, height: row.height, photos: rowPhotos, isPanorama: !!row.isPanorama };
    });

    return { rows: outRows, fillRatio: coveredArea / pageArea };
  }

  // --- Row mode vs. column mode ----------------------------------------
  // Row mode (the whole function above) justifies photos into full-WIDTH
  // rows - the natural fit for a landscape-shaped page and
  // landscape/mixed photos. Measured a poor fit for a portrait-dominant
  // page: a 5-photo all-9:16 set filled only 48% of an 8x11 page in row
  // mode. Column mode is the exact transpose - justify into full-HEIGHT
  // columns instead - and measured dramatically better on the same cases
  // (88% on that same 5-photo set) for the same reason the tree engine
  // already prefers vertical (column) splits for a homogeneous-portrait
  // group: portraits share width poorly but share height well.
  //
  // Implemented as a literal transpose rather than a parallel
  // implementation: swap page W/H, invert every photo's aspect (1/a),
  // run the exact same row-mode function, then swap x<->y and w<->h back
  // on the result. Guarantees column mode can never drift out of sync
  // with row mode's own fixes (the cap, the shrink-to-fit, the panorama
  // carve-out all apply identically, just along the other axis) since
  // there's only one real implementation underneath either mode.
  function transposeBoxes(rows) {
    return rows.map(function (row) {
      return {
        isPanorama: row.isPanorama,
        photos: row.photos.map(function (box) {
          return { uuid: box.uuid, x: box.y, y: box.x, w: box.h, h: box.w };
        })
      };
    });
  }

  // --- Landscape/portrait zone split ------------------------------------
  // Direct feedback, 2026-09-19, from real screenshots: a lone portrait
  // (or a couple) mixed into an otherwise-landscape page kept landing
  // either isolated in its own capped row (dead space on both sides - a
  // solo row that can't span the full width has nowhere else for that
  // width to go) or squeezed into a shared row with landscapes (rejected
  // by partitionAreaRatio's own floor once that's bad enough, which just
  // pushes it back to the isolated-row case). The same isolated-portrait
  // shape recurred across many different Shuffle seeds on the same real
  // page - not a rare unlucky pick, closer to the actual best option
  // available to a SHARED row/column search across two orientations that
  // don't share a row or column shape well.
  //
  // Fix: give portraits their own dedicated width instead of making them
  // compete for a shape landscapes already own. The landscape/main zone
  // keeps doing exactly what already works well - unaffected, since an
  // all-landscape page never reaches ZONE_SPLIT_MIN_PORTRAIT and this
  // function is never called for one at all.
  //
  // Only triggers for a genuine mix: ZONE_SPLIT_MIN_MAIN (2) keeps this
  // from firing on an all/near-all-portrait page (already served by
  // column mode, a separate piece of work) and ZONE_SPLIT_MIN_PORTRAIT
  // (1) means even a single stray portrait gets its own clean strip
  // rather than the isolated-row treatment above.
  //
  // Zone width: the portrait zone gets a share of the page proportional
  // to its own share of the photo COUNT (how much content it actually
  // has to hold), clamped to [0.22, 0.45] - unclamped, a page that's
  // mostly portrait but still has 2 landscapes could give portraits 90%+
  // of the width and starve the landscape zone; a single portrait among
  // 10 landscapes could get squeezed to near nothing.
  //
  // Tray order: preserved WITHIN each zone (landscapes keep their
  // relative order among themselves; portraits keep theirs), but the two
  // groups are now spatially separated rather than interleaved - a
  // bounded trade-off, not a break from "tray order is the book's
  // sequence": the panorama carve-out already does the same thing (pulls
  // a photo whose shape doesn't share a row well with its neighbours out
  // into its own dedicated treatment) for the same underlying reason.
  const ZONE_SPLIT_MIN_MAIN = 2;
  const ZONE_SPLIT_MIN_PORTRAIT = 1;
  const ZONE_PORTRAIT_SHARE_MIN = 0.22;
  const ZONE_PORTRAIT_SHARE_MAX = 0.45;

  function offsetBoxes(rows, dx, dy) {
    return rows.map(function (row) {
      return {
        isPanorama: row.isPanorama,
        photos: row.photos.map(function (box) {
          return { uuid: box.uuid, x: box.x + dx, y: box.y + dy, w: box.w, h: box.h };
        })
      };
    });
  }

  // One zone's own content within a zoneWidth x zoneHeight rectangle
  // that's ALREADY the usable space (no further margin - the outer
  // margin was already applied once, by the caller, to the whole page).
  // A single photo is centered at its own true aspect within the zone,
  // NOT the whole-page full-bleed hero treatment buildJustifiedLayout's
  // own n=1 case uses (that's correct for a photo that IS the entire
  // page's content; wrong here, where it's a fraction of one and
  // stretching it to fill the zone regardless of aspect would silently
  // reintroduce letterboxing/cropping downstream, the one thing this
  // whole engine exists to avoid). 2+ photos go through the normal auto
  // row/column search, scoped to the zone's own rectangle.
  function buildZoneContent(photos, zoneWidth, zoneHeight, gapMm, seed) {
    if (photos.length === 0) return { rows: [], coveredArea: 0 };
    if (photos.length === 1) {
      const rows = scorePartition([photos], zoneWidth, zoneHeight, gapMm).rows;
      const row = rows[0];
      const w = row.height * photos[0].aspect;
      const x = Math.max(zoneWidth - w, 0) / 2;
      const y = Math.max(zoneHeight - row.height, 0) / 2;
      const box = { uuid: photos[0].uuid, x: x, y: y, w: w, h: row.height };
      return { rows: [{ y: y, height: row.height, photos: [box], isPanorama: false }], coveredArea: w * row.height };
    }
    const result = buildJustifiedLayout(photos, zoneWidth, zoneHeight, { marginMm: 0, gapMm: gapMm, variationSeed: seed });
    return { rows: result.rows, coveredArea: result.fillRatio * zoneWidth * zoneHeight };
  }

  function buildZoneSplitLayout(photos, pageWidthMm, pageHeightMm, opts) {
    const marginMm = opts.marginMm === undefined ? 5 : opts.marginMm;
    const gapMm = opts.gapMm === undefined ? 2.5 : opts.gapMm;
    const usableWidth = pageWidthMm - 2 * marginMm;
    const usableHeight = pageHeightMm - 2 * marginMm;

    const portraitPhotos = photos.filter(function (p) { return p.aspect < 0.95; });
    const mainPhotos = photos.filter(function (p) { return !(p.aspect < 0.95); });

    const portraitShare = Math.min(ZONE_PORTRAIT_SHARE_MAX, Math.max(ZONE_PORTRAIT_SHARE_MIN, portraitPhotos.length / photos.length));
    const portraitZoneWidth = usableWidth * portraitShare - gapMm / 2;
    const mainZoneWidth = usableWidth - portraitZoneWidth - gapMm;

    // Distinct salts so a page that ALSO has panorama/variety machinery
    // active inside either zone doesn't coincidentally land both zones
    // on the same pool index.
    const mainSeed = opts.variationSeed === undefined ? undefined : mixSeed(opts.variationSeed, 701);
    const portraitSeed = opts.variationSeed === undefined ? undefined : mixSeed(opts.variationSeed, 907);

    const mainContent = buildZoneContent(mainPhotos, mainZoneWidth, usableHeight, gapMm, mainSeed);
    const portraitContent = buildZoneContent(portraitPhotos, portraitZoneWidth, usableHeight, gapMm, portraitSeed);

    // Portrait zone sits on the right of the main zone, consistently -
    // simpler and more predictable than trying to infer a side from tray
    // order, and matches how the two zones are computed above (main
    // zone's own x starts at the page margin; portrait zone starts right
    // after it plus the inter-zone gap).
    const mainRows = offsetBoxes(mainContent.rows, marginMm, marginMm);
    const portraitRows = offsetBoxes(portraitContent.rows, marginMm + mainZoneWidth + gapMm, marginMm);

    const pageArea = pageWidthMm * pageHeightMm;
    const fillRatio = (mainContent.coveredArea + portraitContent.coveredArea) / pageArea;

    return { rows: mainRows.concat(portraitRows), fillRatio: fillRatio, mode: 'zone-split' };
  }

  // Row vs. column mode used to be a cheap portrait/landscape majority
  // vote (more portraits on the page -> column, otherwise row). Dropped
  // in favour of actually building both and keeping whichever fills the
  // page better - measured on a real reported case (4 portraits + 1
  // landscape) that the vote's column-mode pick put the landscape alone
  // in a crowded group and rendered it 4.7x smaller than the other
  // photos, while row mode's own [3,2] split for the SAME five photos
  // gave every photo a reasonable, proportionate size (fillRatio 0.65 vs
  // column's 0.43 once column's own partition search is balance-
  // constrained the same way - see isBalancedPartition). A pure
  // orientation count has no way to know a mode it favours will actually
  // produce a worse result for THIS specific aspect mix; fillRatio,
  // already computed by both paths, does. Still picks column for a
  // genuine all/mostly-portrait page exactly as before (that case's own
  // measured 48%->88% fill gain, docs/decisions-to-make.md #30, holds
  // because column mode's fillRatio legitimately wins there too) - this
  // is a strictly more accurate version of the same idea, not a
  // different one.
  function buildJustifiedLayout(photos, pageWidthMm, pageHeightMm, opts) {
    opts = opts || {};
    if (photos.length <= 1) {
      const result = buildJustifiedLayoutRowMode(photos, pageWidthMm, pageHeightMm, opts);
      result.mode = 'row';
      return result;
    }

    if (opts.mode === 'row' || opts.mode === 'column') {
      if (opts.mode === 'column') {
        const transposedPhotos = photos.map(function (p) { return { uuid: p.uuid, aspect: 1 / p.aspect }; });
        const result = buildJustifiedLayoutRowMode(transposedPhotos, pageHeightMm, pageWidthMm, opts);
        return { rows: transposeBoxes(result.rows), fillRatio: result.fillRatio, mode: 'column' };
      }
      const result = buildJustifiedLayoutRowMode(photos, pageWidthMm, pageHeightMm, opts);
      result.mode = 'row';
      return result;
    }

    const rowResult = buildJustifiedLayoutRowMode(photos, pageWidthMm, pageHeightMm, opts);
    rowResult.mode = 'row';

    const transposedPhotos = photos.map(function (p) { return { uuid: p.uuid, aspect: 1 / p.aspect }; });
    const columnRaw = buildJustifiedLayoutRowMode(transposedPhotos, pageHeightMm, pageWidthMm, opts);
    const columnResult = { rows: transposeBoxes(columnRaw.rows), fillRatio: columnRaw.fillRatio, mode: 'column' };

    let best = columnResult.fillRatio > rowResult.fillRatio ? columnResult : rowResult;

    // Zone-split is a THIRD candidate, not a replacement for the other
    // two - only evaluated (extra cost, so gated) on a genuine mix, and
    // only wins if it measurably fills better than whichever of
    // row/column already won, same "real objective, not an assumption"
    // principle as row-vs-column above. See buildZoneSplitLayout's own
    // comment for the full rationale and the real report this answers.
    const portraitPhotos = photos.filter(function (p) { return p.aspect < 0.95; });
    const mainPhotos = photos.filter(function (p) { return !(p.aspect < 0.95); });
    if (mainPhotos.length >= ZONE_SPLIT_MIN_MAIN && portraitPhotos.length >= ZONE_SPLIT_MIN_PORTRAIT) {
      const zoneResult = buildZoneSplitLayout(photos, pageWidthMm, pageHeightMm, opts);
      if (zoneResult.fillRatio > best.fillRatio) best = zoneResult;
    }

    return best;
  }

  const JustifiedLayout = {
    buildJustifiedLayout: buildJustifiedLayout,
    solveRowHeight: solveRowHeight,
    bestRowPartition: bestRowPartition,
    PANORAMA_ASPECT_THRESHOLD: PANORAMA_ASPECT_THRESHOLD
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = JustifiedLayout;
  } else {
    root.JustifiedLayout = JustifiedLayout;
  }
})(typeof window !== 'undefined' ? window : this);
